/**
 * Flight Results Component
 * Handles display and interaction with flight search results
 */

import { FlightResult, FlightFilters } from '../types/data-models';

export class FlightResultsComponent {
  private container: HTMLElement;
  private allFlights: FlightResult[] = [];
  private filteredFlights: FlightResult[] = [];
  private currentPage = 1;
  private itemsPerPage = 10;
  private currentSort = 'price';

  public onFlightSelect: ((flightId: string) => void) | null = null;
  public onSortChange: ((sortBy: string) => void) | null = null;
  public onPageChange: ((page: number) => void) | null = null;

  constructor(container: HTMLElement) {
    this.container = container;
    this.bindEvents();
  }

  private bindEvents(): void {
    // Sort dropdown
    const sortSelect = document.getElementById('sort-by') as HTMLSelectElement;
    if (sortSelect) {
      sortSelect.addEventListener('change', (e) => {
        const target = e.target as HTMLSelectElement;
        this.currentSort = target.value;
        this.sortResults(this.currentSort);
        if (this.onSortChange) {
          this.onSortChange(this.currentSort);
        }
      });
    }
  }

  public displayFlights(flights: FlightResult[]): void {
    this.allFlights = flights;
    this.filteredFlights = [...flights];
    this.currentPage = 1;
    this.sortResults(this.currentSort);
    this.renderFlights();
    this.renderPagination();
  }

  public applyFilters(filters: FlightFilters): void {
    this.filteredFlights = this.allFlights.filter(flight => {
      // Price filter
      if (filters.priceRange) {
        if (flight.price.min < filters.priceRange.min || flight.price.min > filters.priceRange.max) {
          return false;
        }
      }

      // Airlines filter
      if (filters.airlines && filters.airlines.length > 0) {
        if (!filters.airlines.includes(flight.airline)) {
          return false;
        }
      }

      // Stops filter
      if (filters.stops && filters.stops.length > 0) {
        if (!filters.stops.includes(flight.stops)) {
          return false;
        }
      }

      // Departure time filter
      if (filters.departureTimeRanges && filters.departureTimeRanges.length > 0) {
        const hour = flight.departureTime.getHours();
        const timeRange = this.getTimeRange(hour);
        if (!filters.departureTimeRanges.includes(timeRange)) {
          return false;
        }
      }

      return true;
    });

    this.currentPage = 1;
    this.renderFlights();
    this.renderPagination();
  }

  private getTimeRange(hour: number): string {
    if (hour >= 6 && hour < 12) return 'morning';
    if (hour >= 12 && hour < 18) return 'afternoon';
    if (hour >= 18 && hour < 24) return 'evening';
    return 'night';
  }

  public sortResults(sortBy: string): void {
    this.currentSort = sortBy;
    
    this.filteredFlights.sort((a, b) => {
      switch (sortBy) {
        case 'price':
          return a.price.min - b.price.min;
        case 'duration':
          return this.parseDuration(a.duration) - this.parseDuration(b.duration);
        case 'departure':
          return a.departureTime.getTime() - b.departureTime.getTime();
        case 'arrival':
          return a.arrivalTime.getTime() - b.arrivalTime.getTime();
        default:
          return 0;
      }
    });

    this.renderFlights();
  }

  private parseDuration(duration: string): number {
    const match = duration.match(/(\d+)h\s*(\d+)?min?/);
    if (match) {
      const hours = parseInt(match[1] || '0') || 0;
      const minutes = parseInt(match[2] || '0') || 0;
      return hours * 60 + minutes;
    }
    return 0;
  }

  public goToPage(page: number): void {
    this.currentPage = page;
    this.renderFlights();
    this.renderPagination();
    
    // Scroll to top of results
    this.container.scrollIntoView({ behavior: 'smooth' });
    
    if (this.onPageChange) {
      this.onPageChange(page);
    }
  }

  private renderFlights(): void {
    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    const endIndex = startIndex + this.itemsPerPage;
    const pageFlights = this.filteredFlights.slice(startIndex, endIndex);

    this.container.innerHTML = pageFlights.map(flight => this.createFlightCard(flight)).join('');

    // Bind click events to flight cards
    this.container.querySelectorAll('.flight-card').forEach((card, index) => {
      const flight = pageFlights[index];
      if (flight) {
        const selectButton = card.querySelector('.flight-card__select') as HTMLButtonElement;
        
        if (selectButton && this.onFlightSelect) {
          selectButton.addEventListener('click', () => {
            if (this.onFlightSelect) {
              this.onFlightSelect(flight.id);
            }
          });
        }
      }
    });
  }

  private createFlightCard(flight: FlightResult): string {
    const departureTime = flight.departureTime.toLocaleTimeString('fr-FR', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
    const arrivalTime = flight.arrivalTime.toLocaleTimeString('fr-FR', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });

    const stopsText = flight.stops === 0 ? 'Direct' : 
                     flight.stops === 1 ? '1 escale' : 
                     `${flight.stops} escales`;

    const stopDetails = flight.stopDetails && flight.stopDetails.length > 0 
      ? `<div class="flight-card__stops-details">via ${flight.stopDetails.join(', ')}</div>`
      : '';

    return `
      <article class="flight-card" data-flight-id="${flight.id}">
        <div class="flight-card__header">
          <div class="flight-card__airline">
            <div class="flight-card__airline-logo">
              <img src="${flight.airlineLogo}" alt="${flight.airline}" class="flight-card__logo-image" 
                   onerror="this.style.display='none'; this.nextElementSibling.style.display='block';">
              <div class="flight-card__logo-placeholder" style="display: none;">
                [Logo ${flight.airline}]
              </div>
            </div>
            <div class="flight-card__airline-info">
              <div class="flight-card__airline-name">${flight.airline}</div>
              <div class="flight-card__flight-number">${flight.flightNumber}</div>
            </div>
          </div>
          <div class="flight-card__price">
            <div class="flight-card__price-amount">€${flight.price.min}</div>
            <div class="flight-card__price-label">par personne</div>
          </div>
        </div>

        <div class="flight-card__route">
          <div class="flight-card__departure">
            <div class="flight-card__time">${departureTime}</div>
            <div class="flight-card__airport">
              <span class="flight-card__city">${flight.origin}</span>
              <span class="flight-card__code">${flight.originCode}</span>
            </div>
          </div>

          <div class="flight-card__journey">
            <div class="flight-card__duration">${flight.duration}</div>
            <div class="flight-card__route-line">
              <div class="flight-card__route-dots">
                ${flight.stops > 0 ? '●'.repeat(flight.stops) : ''}
              </div>
            </div>
            <div class="flight-card__stops">${stopsText}</div>
            ${stopDetails}
          </div>

          <div class="flight-card__arrival">
            <div class="flight-card__time">${arrivalTime}</div>
            <div class="flight-card__airport">
              <span class="flight-card__city">${flight.destination}</span>
              <span class="flight-card__code">${flight.destinationCode}</span>
            </div>
          </div>
        </div>

        <div class="flight-card__details">
          <div class="flight-card__info">
            <div class="flight-card__aircraft">${flight.aircraft}</div>
            <div class="flight-card__baggage">
              <span class="flight-card__baggage-item">Bagage cabine: ${flight.baggage.carry}</span>
              <span class="flight-card__baggage-item">Bagage soute: ${flight.baggage.checked}</span>
            </div>
            ${flight.amenities.length > 0 ? `
              <div class="flight-card__amenities">
                ${flight.amenities.map(amenity => `<span class="flight-card__amenity">${amenity}</span>`).join('')}
              </div>
            ` : ''}
          </div>
          <div class="flight-card__actions">
            <button type="button" class="btn btn-primary flight-card__select">
              Sélectionner ce vol
            </button>
          </div>
        </div>
      </article>
    `;
  }

  private renderPagination(): void {
    const totalPages = Math.ceil(this.filteredFlights.length / this.itemsPerPage);
    const paginationContainer = document.getElementById('pagination');
    const paginationPages = document.getElementById('pagination-pages');

    if (!paginationContainer || !paginationPages) return;

    if (totalPages <= 1) {
      paginationContainer.style.display = 'none';
      return;
    }

    paginationContainer.style.display = 'flex';

    // Generate page numbers
    const pages: string[] = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, this.currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    if (startPage > 1) {
      pages.push(`<button type="button" class="pagination__page" data-page="1">1</button>`);
      if (startPage > 2) {
        pages.push(`<span class="pagination__ellipsis">...</span>`);
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      const isActive = i === this.currentPage;
      pages.push(`
        <button type="button" class="pagination__page ${isActive ? 'pagination__page--active' : ''}" 
                data-page="${i}" ${isActive ? 'aria-current="page"' : ''}>
          ${i}
        </button>
      `);
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pages.push(`<span class="pagination__ellipsis">...</span>`);
      }
      pages.push(`<button type="button" class="pagination__page" data-page="${totalPages}">${totalPages}</button>`);
    }

    paginationPages.innerHTML = pages.join('');

    // Bind page click events
    paginationPages.querySelectorAll('.pagination__page').forEach(button => {
      button.addEventListener('click', (e) => {
        const target = e.target as HTMLButtonElement;
        const pageStr = target.dataset.page;
        const page = pageStr ? parseInt(pageStr) : 1;
        this.goToPage(page);
      });
    });

    // Previous/Next buttons
    const prevButton = paginationContainer.querySelector('.pagination__prev') as HTMLButtonElement;
    const nextButton = paginationContainer.querySelector('.pagination__next') as HTMLButtonElement;

    if (prevButton) {
      prevButton.disabled = this.currentPage === 1;
      prevButton.onclick = () => {
        if (this.currentPage > 1) {
          this.goToPage(this.currentPage - 1);
        }
      };
    }

    if (nextButton) {
      nextButton.disabled = this.currentPage === totalPages;
      nextButton.onclick = () => {
        if (this.currentPage < totalPages) {
          this.goToPage(this.currentPage + 1);
        }
      };
    }
  }

  public getDisplayedFlights(): FlightResult[] {
    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    const endIndex = startIndex + this.itemsPerPage;
    return this.filteredFlights.slice(startIndex, endIndex);
  }

  public getTotalResults(): number {
    return this.filteredFlights.length;
  }

  public getCurrentPage(): number {
    return this.currentPage;
  }

  public getTotalPages(): number {
    return Math.ceil(this.filteredFlights.length / this.itemsPerPage);
  }
}